import { serverReachability } from '@/shared/lib/server-reachability'

export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly fields?: Record<string, string[]>
  readonly context?: Record<string, unknown>

  constructor(
    message: string,
    code: string,
    status: number,
    fields?: Record<string, string[]>,
    context?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.fields = fields
    this.context = context
  }
}

class ApiClient {
  /** Shared response handler for all requests. */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (response.status === 401) {
      throw new ApiError('غير مصرّح', 'UNAUTHORIZED', 401)
    }

    if (response.status === 204) {
      return undefined as T
    }

    const data = await response.json()

    if (!response.ok) {
      const error = data?.error ?? {}
      throw new ApiError(
        error.message ?? 'خطأ غير متوقع',
        error.code ?? 'INTERNAL_ERROR',
        response.status,
        error.fields,
        error.context,
      )
    }

    return data as T
  }

  /** JSON request — used by get, post, put, patch, delete. */
  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    params?: Record<string, unknown>,
  ): Promise<T> {
    const url = new URL(path, window.location.origin)
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null && value !== '') {
          url.searchParams.set(key, String(value))
        }
      }
    }

    const headers: Record<string, string> = {}
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json'
    }

    let response: Response
    try {
      response = await fetch(url.toString(), {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        credentials: 'same-origin',
      })
    } catch (error) {
      // TypeError = network failure (WiFi down, DNS fail, server unreachable)
      serverReachability.reportFailure()
      throw error
    }

    // Got a response — server is reachable (even if it returned an error status)
    serverReachability.reportSuccess()
    return this.handleResponse<T>(response)
  }

  async get<T>(path: string, params?: Record<string, unknown>): Promise<T> {
    return this.request<T>('GET', path, undefined, params)
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, body)
  }

  async put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PUT', path, body)
  }

  async patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PATCH', path, body)
  }

  async delete(path: string): Promise<void> {
    return this.request<void>('DELETE', path)
  }

  /** Multipart upload — for CSV imports. */
  async upload<T>(path: string, formData: FormData): Promise<T> {
    let response: Response
    try {
      response = await fetch(path, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
      })
    } catch (error) {
      serverReachability.reportFailure()
      throw error
    }

    serverReachability.reportSuccess()
    return this.handleResponse<T>(response)
  }
}

export const apiClient = new ApiClient()
